import { Record as record } from 'immutable';
import joi from 'joi';
import _ from 'lodash';
import { v1 as uuidv1 } from 'uuid';

import t from '../../../../src/lib/I18n';
import config from '../../../../src/config';
import { construct, resolve, fields } from '../../../../src/helpers/LinkMapper';
import { getDefaults, getResolvers } from '../../../../src/helpers/Records';
import ValidationMixin from '../../../../src/records/ValidationMixin';
import validators from '../../../../src/validation';

import EvidenceType from './EvidenceType';
import NonSeasonTicketType from './NonSeasonTicketType';

const fieldSchema = {
    id: [fields.string],
    type: [resolve.as(EvidenceType)],
    ticketType: [resolve.as(NonSeasonTicketType)],
    comments: [fields.string],
    file: [resolve.with(Object)],
    xhr: [resolve.with(Object)],
    uploadFailed: [fields.bool],
    skipFileUpload: [fields.bool],
    imageId: [fields.number]
};

export default class TicketEvidence extends record(getDefaults(fieldSchema)) {
    constructor(values = {}) {
        super(_.defaults({}, values, { id: uuidv1() }));
    }

    static fromJSON(json = {}) {
        return construct(TicketEvidence, json, getResolvers(fieldSchema));
    }

    toJS() {
        // window.File is not serialisable, so convert to a plain object
        const { file } = this;
        let evidence = this;
        if (file && !this.imageId) {
            evidence = this.set('file', null);
        }
        if (file && this.imageId) {
            evidence = this.set('file', {
                name: file.name,
                type: file.type,
                size: file.size
            });
        }
        return super.toJS.call(evidence);
    }

    getComputedValues() {
        return {
            type: Boolean(this.type),
            ticketType: Boolean(this.ticketType),
            fileSize: this.file && this.file.size,
            fileType: this.file && this.file.type
        };
    }
    getSchema() {
        const { type, ticketType } = this;
        const needsUpload =
            ticketType &&
            !ticketType.evidenceNotRequired &&
            !ticketType.evidenceIsOptional &&
            !this.skipFileUpload;
        const mbTobytes = mb => (mb || 0) * 1024 ** 2;
        const uploadTypes = config.get('delayRepayUploadTypes') || [];
        const maxUploadSize = mbTobytes(config.get('delayRepayMaxUploadMb'));
        const minUploadSize = mbTobytes(config.get('delayRepayMinUploadMb'));
        return {
            type: (ticketType.evidenceNotRequired || ticketType.evidenceIsOptional)
                ? joi.any()
                : validators.true(),
            ticketType: validators.true(),
            comments: _.get(type, 'comments')
                ? validators.requiredString(10, 500)
                : joi.any(),
            ...(needsUpload
                ? {
                      imageId: validators.required(),
                      file: validators.required(),
                      fileType: validators.allowOnly(uploadTypes).messages({
                          'any.only': t('delayrepay.error.fileType'),
                          'string.base': t('delayrepay.error.fileType'),
                      }),
                      fileSize: joi
                          .number()
                          .integer()
                          .min(minUploadSize)
                          .max(maxUploadSize)
                          .required()
                          .messages({
                              'any.required': t('alertmessage.required'),
                              'number.base': t('alertmessage.required'),
                              'number.min': t('delayrepay.error.uploadSize.min'),
                              'number.max': t('delayrepay.error.uploadSize.max'),
                          })
                  }
                : {})
        };
    }

    isUploading() {
        return !!this.xhr;
    }

    isOfType(type) {
        return _.isEqual(this.getIn(['type', 'id']), type);
    }
}

Object.assign(TicketEvidence.prototype, ValidationMixin);
